import { type FocusEvent, useCallback, useEffect } from 'react';
import type { Key } from 'react-aria-components';
import { AddressFields } from '@/components/AddressFields';
import { CheckboxDisclosure } from '@/components/CheckboxDisclosure';
import {
  FieldName,
  ValidationMessage,
  ValidationSectionKey,
} from '@/constants';
import { usePageData } from '@/contexts/pageContext';
import { useShipping } from '@/contexts/shippingContext';
import type { RequiredFormField } from '@/contexts/types';
import { useValidation } from '@/contexts/validationContext';
import { useGetCounties } from '@/hooks/useGetCounties';
import type { AddressFieldKey } from '@/types';
import { doNothingOnSubmit } from '@/utils/doNothingOnSubmit';
import { getCountyLabel } from '@/utils/getCountyLabel';
import { getPostalCodeLabel } from '@/utils/getPostalCodeLabel';
import { getRequiredAddressFields } from '@/utils/getRequiredAddressFields';
import { validatePhone } from '@/utils/validatePhone';

export const BillingAddressDisclosure = () => {
  const { countries, excludedCountriesForBilling } = usePageData();
  const {
    isBillingSameAsShipping,
    orderPayload: { billingAddress },
    setBillingAddress,
    setIsBillingSameAsShipping,
  } = useShipping();

  const { countryCode } = billingAddress;
  const counties = useGetCounties(countryCode);
  const countyLabel = getCountyLabel(countryCode);
  const postalCodeLabel = getPostalCodeLabel(countryCode);

  const {
    addressFieldErrors,
    clearAddressFieldError,
    clearValidationError,
    errorMessages,
    setAddressFieldError,
  } = useValidation();

  const addressError = errorMessages[ValidationSectionKey.BillingAddress];

  const validationErrors = addressFieldErrors.billing;

  useEffect(() => {
    if (Object.values(validationErrors).filter(Boolean).length === 0) {
      clearValidationError(ValidationSectionKey.BillingAddress);
    }
  }, [clearValidationError, validationErrors]);

  const updateValidation = useCallback(
    (fieldName: RequiredFormField, value: string) => {
      // Phone validation: check format and length
      if (fieldName === FieldName.Phone) {
        if (!validatePhone(value)) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.InvalidPhone,
          );
        } else if (value.length > 19) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.PhoneTooLong,
          );
        } else {
          clearAddressFieldError('billing', fieldName);
        }
      } else if (fieldName === FieldName.FirstName) {
        if (value.length > 20) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.FirstNameTooLong,
          );
        } else {
          clearAddressFieldError('billing', fieldName);
        }
      } else if (fieldName === FieldName.LastName) {
        if (value.length > 20) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.LastNameTooLong,
          );
        } else {
          clearAddressFieldError('billing', fieldName);
        }
      } else if (fieldName === FieldName.AddressLine1) {
        if (value.length > 50) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.AddressLineTooLong,
          );
        } else {
          clearAddressFieldError('billing', fieldName);
        }
      } else if (fieldName === FieldName.City) {
        if (value.length > 50) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.CityTooLong,
          );
        } else {
          clearAddressFieldError('billing', fieldName);
        }
      }
      // For other fields (postalCode, countryCode, etc.), don't clear errors here
      // They have their own validation logic
    },
    [clearAddressFieldError, setAddressFieldError],
  );

  const handlePhoneBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (value) {
        updateValidation(FieldName.Phone, value);
      } else {
        setAddressFieldError(
          'billing',
          FieldName.Phone,
          ValidationMessage.MissingField,
        );
      }
    },
    [setAddressFieldError, updateValidation],
  );

  const handleChange = useCallback(
    (fieldName: AddressFieldKey) => (value: Key | null) => {
      const rawValue = typeof value === 'string' ? value : '';
      const normalisedValue = rawValue.trim();

      // Validate raw length (including spaces) before trimming for length-constrained fields
      if (
        fieldName === FieldName.FirstName ||
        fieldName === FieldName.LastName ||
        fieldName === FieldName.Phone ||
        fieldName === FieldName.AddressLine1 ||
        fieldName === FieldName.City
      ) {
        let errorMessage: string | null = null;
        if (fieldName === FieldName.FirstName && rawValue.length > 20) {
          errorMessage = ValidationMessage.FirstNameTooLong;
        } else if (fieldName === FieldName.LastName && rawValue.length > 20) {
          errorMessage = ValidationMessage.LastNameTooLong;
        } else if (fieldName === FieldName.Phone && rawValue.length > 19) {
          errorMessage = ValidationMessage.PhoneTooLong;
        } else if (
          fieldName === FieldName.AddressLine1 &&
          rawValue.length > 50
        ) {
          errorMessage = ValidationMessage.AddressLineTooLong;
        } else if (fieldName === FieldName.City && rawValue.length > 50) {
          errorMessage = ValidationMessage.CityTooLong;
        }

        if (errorMessage) {
          setAddressFieldError(
            'billing',
            fieldName as RequiredFormField,
            errorMessage,
          );
        }
      }

      const requiredFields = getRequiredAddressFields(countryCode);

      const isRequired = requiredFields.some(key => key === fieldName);

      // Validation only for required fields
      if (isRequired) {
        if (normalisedValue) {
          // For length-constrained fields, use raw value for length validation
          // For phone, always use raw value to check total length including formatting
          const isLengthConstrained =
            fieldName === FieldName.FirstName ||
            fieldName === FieldName.LastName ||
            fieldName === FieldName.Phone ||
            fieldName === FieldName.AddressLine1 ||
            fieldName === FieldName.City;
          const valueToValidate =
            fieldName === FieldName.Phone || isLengthConstrained
              ? rawValue
              : normalisedValue;
          updateValidation(fieldName as RequiredFormField, valueToValidate);
        } else {
          setAddressFieldError(
            'billing',
            fieldName as RequiredFormField,
            ValidationMessage.MissingField,
          );
        }
      }
      // Note: Error clearing for length-constrained fields is handled by updateValidation
      // No need to clear errors here to avoid conflicts

      // Address update for ALL fields (required and optional)
      // Store trimmed value for text fields to prevent storing leading/trailing spaces
      if (fieldName === FieldName.Country) {
        const selectedCountry = countries?.find(
          country => value === country.countryCode,
        );
        if (selectedCountry) {
          // When country changes, also clear any previously selected county
          // so the user must select a valid county for the new country
          setBillingAddress({
            [FieldName.Country]: selectedCountry.countryCode,
            [FieldName.County]: '',
          });
        }
      } else if (fieldName === FieldName.County) {
        const selectedCounty = counties?.find(
          county => value === county.countyCode,
        );
        if (selectedCounty) {
          setBillingAddress({
            [FieldName.County]: selectedCounty.countyCode,
          });
        }
      } else {
        // Store trimmed value for text fields to avoid storing leading/trailing spaces
        const valueToStore =
          typeof value === 'string' ? normalisedValue : value;
        setBillingAddress({ [fieldName]: valueToStore });
      }
    },
    [
      counties,
      countries,
      countryCode,
      clearAddressFieldError,
      setAddressFieldError,
      setBillingAddress,
      updateValidation,
    ],
  );

  const handleClick = () => {
    setIsBillingSameAsShipping(currentState => !currentState);
  };

  return (
    <CheckboxDisclosure
      className={addressError ? 'has-validation-error' : undefined}
      handleClick={handleClick}
      isExpanded={!isBillingSameAsShipping}
      isSelected={isBillingSameAsShipping}
      label="Use shipping address as billing address"
      name={ValidationSectionKey.BillingAddress}
    >
      {addressError ? (
        <p aria-live="assertive" className="validation-error">
          {addressError}
        </p>
      ) : null}
      <AddressFields
        address={billingAddress}
        countyLabel={countyLabel}
        excludedBillingCountryCodes={excludedCountriesForBilling}
        handleChange={handleChange}
        handlePhoneBlur={handlePhoneBlur}
        handleSubmit={doNothingOnSubmit}
        isBillingAddress={true}
        postalCodeLabel={postalCodeLabel}
        validationErrors={validationErrors}
      />
    </CheckboxDisclosure>
  );
};
